import * as React from "react";
import CircularProgress from "material-ui/CircularProgress";
import { merge, $, media } from "glamor";
import theme from "styles/theme";
import classNames from "classnames";
import { format } from "lib/globalize-util";
import Button from "components/form/button";
import CartIcon from "components/icons/cart";
import getInitials from "lib/avatar-initials";
import Avatar from "material-ui/Avatar";
import WarningIcon from "material-ui/svg-icons/alert/warning";
import ChevronRightIcon from "material-ui/svg-icons/navigation/chevron-right";
import ModePicker from "./mode-picker";
import type { IScope } from "reducers/scope";
import { IOrder } from "records/order";
import Badge from "material-ui/Badge";
import Company from "models/company";
import { DATATEST } from "constants/integration";
import { InlineLoading } from "../../content-loading";

export interface ICart {
    result: string;
    isFetching: boolean;
    isFetchingPricing: boolean;
    items?: any;
    entities: {
        [key: string]: IOrder;
    };
}

interface IProps {
    cart: ICart;
    scope: IScope;
    disabled: boolean;
    legacyLoaded: boolean;
    canOrder: boolean;
    addingItems: boolean;
    openNavSection: string;
    onHandleStartOrderOpen: () => void;
    onToggleCart: () => void;
    modePickerOpen: boolean;
    onModeCancel: () => void;
    onModeSelect: (mode: string, options?: any) => void;
    companies: { [key: string]: typeof Company };
}

export const Cart: React.FunctionComponent<IProps> = ({
    cart,
    disabled = false,
    legacyLoaded,
    scope,
    canOrder,
    addingItems,
    openNavSection,
    onHandleStartOrderOpen,
    onToggleCart,
    modePickerOpen,
    onModeCancel,
    onModeSelect,
    companies,
}) => {
    const styles = React.useMemo(() => {
        return {
            base: merge(
                {
                    display: "flex",
                    flexDirection: "row",
                    ":hover": {
                        backgroundColor: "rgba(153, 153, 153, 0.2)", // match MUI button hover
                    },
                },
                $(" .action", {
                    position: "relative",
                    marginLeft: "0px",
                    marginRight: "8px",
                }),
                $(" .loadingCart", {
                    marginLeft: "14px",
                    marginRight: "18px",
                    paddingTop: "23px",
                    transition: "padding-top 0.3s ease",
                }),
                $(" .loadingCartPricing", {
                    margin: "0 15px",
                    top: "8px",
                    display: "inline-block",
                    position: "relative",
                }),
                $(" .total", {
                    color: theme.palette.primary1Color,
                    paddingLeft: "5px",
                }),
                $(" .spacer", {
                    width: "1px",
                    background: theme.palette.lighterGrey,
                    margin: "14px 8px 0",
                    height: "32px",
                }),
                $(" .orderingFor", {
                    color: theme.palette.grey,
                    width: "50px",
                    textAlign: "right",
                }),
                $(" .companyContainer", {
                    display: "flex",
                    flexDirection: "row",
                    paddingTop: "15px",
                    height: "60px",
                    cursor: "pointer",
                }),
                $(" .cartButton", {
                    cursor: "pointer",
                }),
                $(" .buyerName", {
                    fontSize: "14px",
                    fontWeight: "bold",
                    height: "24px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                }),
                $(" .retailerError", {
                    color: theme.palette.error,
                }),
                $(" .companyInfo", {
                    marginTop: "-3px",
                    maxWidth: "110px",
                }),
                $(" .retailerName", {
                    marginTop: "-7px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                }),
                $(" .cartMode", {
                    color: theme.palette.grey,
                    transition: "height 0.3s ease",
                    opacity: 1,
                    whiteSpace: "nowrap",
                    marginTop: "-7px",
                    textAlign: "center",
                }),
                media(
                    "(min-width: 240px) AND (max-width: 466px)",
                    $(" .spacer", {
                        display: "none !important",
                    })
                )
            ),
            cartButton: {
                margin: "10px 10px 0 10px",
            },
            cartInitializedButton: {
                border: "none",
                backgroundColor: "transparent",
                margin: "10px 0 0 10px",
            },
            cartIcon: {
                width: "22px",
                height: "22px",
                top: "50%",
                position: "absolute",
                transform: "translateY(-50%)",
                marginLeft: "20px",
            },
            labelStyle: {
                padding: "0 20px 0 50px",
            },
            avatar: {
                margin: "0 6px",
            },
            count: {
                padding: "0px",
                height: "17px",
                width: "24px",
            },
        };
    }, []);

    const { entities, result } = cart;
    const brandLoaded = scope.brand && scope.brand._id;
    let cartButton;
    if (
        !disabled &&
        cart &&
        legacyLoaded &&
        brandLoaded &&
        canOrder &&
        !addingItems &&
        !cart.isFetching &&
        cart.result.length !== 0
    ) {
        const order = entities.orders[result];

        if (order.__uninitiated_order) {
            cartButton = (
                <Button
                    data-test={DATATEST.WORKING_ORDER.HEADER_CART}
                    label={format("Start Order")}
                    style={styles.cartButton}
                    primary={true}
                    labelPosition="after"
                    labelStyle={styles.labelStyle}
                    icon={<CartIcon style={styles.cartIcon} color={theme.palette.canvasColor} />}
                    onClick={onHandleStartOrderOpen}
                />
            );
        } else {
            let retailerName = order.retailer && order.retailer.retailer_name;
            let buyerName = order.retailer && order.retailer.buyer_name;
            const orderRetailer = order.retailer && order.retailer.ref && companies && companies[order.retailer.ref];
            if (orderRetailer) {
                // setting field values off most current data in company rather
                // than relying on caches data in order.retailer object
                const buyer = orderRetailer.__buyers.find((buyerEntry) => buyerEntry.__ref === order.retailer.buyer);
                buyerName = buyer ? buyer.__name : null;
                retailerName = orderRetailer.__name;
            }
            const hasBuyerOrRetailerMissing = !retailerName || !buyerName;
            cartButton = (
                <React.Fragment>
                    <div className="companyContainer" onClick={onToggleCart}>
                        <div className="orderingFor"> {format("Ordering for:")}</div>
                        <Avatar
                            backgroundColor={hasBuyerOrRetailerMissing ? theme.palette.error : theme.palette.blue}
                            color={theme.palette.canvasColor}
                            size={30}
                            style={styles.avatar}
                        >
                            {hasBuyerOrRetailerMissing ? (
                                <WarningIcon color={theme.palette.canvasColor} />
                            ) : (
                                getInitials(order.retailer.retailer_name)
                            )}
                        </Avatar>
                        <div className="companyInfo">
                            <div
                                className={classNames("buyerName", {
                                    retailerError: !buyerName,
                                })}
                            >
                                {buyerName || format("Select buyer")}
                            </div>
                            <div
                                className={classNames("retailerName", {
                                    retailerError: !retailerName,
                                })}
                            >
                                {retailerName || format("No company selected")}
                            </div>
                        </div>
                        {!buyerName && <ChevronRightIcon color={theme.palette.error} />}
                    </div>
                    <div className="cartButton" onClick={onToggleCart}>
                        <Button
                            label={
                                <React.Fragment>
                                    <Badge
                                        badgeContent={order.line_items_count || 0}
                                        primary={true}
                                        style={styles.count}
                                    />
                                    {cart.isFetchingPricing && (
                                        <InlineLoading size={25} className="loadingCartPricing" />
                                    )}
                                    {!cart.isFetchingPricing && (
                                        <span data-test={DATATEST.TOTAL_LABEL} className="total">
                                            {order._totalLabel}
                                        </span>
                                    )}
                                </React.Fragment>
                            }
                            style={styles.cartInitializedButton}
                            labelPosition="after"
                            labelStyle={styles.labelStyle}
                            icon={<CartIcon style={styles.cartIcon} />}
                            onClick={onToggleCart}
                        />
                        {order.order_flow_type && <div className="cartMode">{order._mode}</div>}
                    </div>
                </React.Fragment>
            );
        }
    } else if (openNavSection === "wholesale" && brandLoaded && canOrder) {
        cartButton = (
            <div className={classNames("action", "loadingCart")}>
                <CircularProgress size={16} thickness={1} color={theme.palette.grey} data-test="header-cart-loading" />
            </div>
        );
    }
    return (
        <div className="cart-button" {...styles.base}>
            {cartButton}
            <ModePicker open={modePickerOpen} onRequestClose={onModeCancel} onSelect={onModeSelect} />
        </div>
    );
};
